/**
 * CampaignsService
 * Domain service bridging the campaigns data service → use cases.
 *
 * Architecture:
 * UseCase (Remapper) → CampaignsService → CampaignsDataService
 *
 * Thin pass-through of raw JsonObject items — the use cases own the remapping,
 * mirroring SubscriptionService. The six ranking pass-throughs expose a single
 * domain-service surface for Task 5's ranking use cases; the live-vs-ended
 * branching (Flutter's CampaignsRepositoryImpl, campaigns_repository_impl.dart)
 * lives in those use cases, not here.
 */

import { CampaignsDataService } from '@/data/services/campaigns/campaignsDataService';
import type { JsonObject } from '@/data/types/json';

export class CampaignsService {
  private static instance: CampaignsService;

  private campaignsDataService: CampaignsDataService;

  private constructor() {
    this.campaignsDataService = CampaignsDataService.getInstance();
  }

  static getInstance(): CampaignsService {
    if (!CampaignsService.instance) {
      CampaignsService.instance = new CampaignsService();
    }
    return CampaignsService.instance;
  }

  /**
   * Active campaigns (startDate <= now), ordered by startDate desc.
   * Returns raw JsonObject items for remapping in the use case.
   */
  async getActiveCampaigns(): Promise<JsonObject[]> {
    return this.campaignsDataService.getActiveCampaigns();
  }

  /**
   * Live user leaderboard ordered by subscribers.
   * The live query is global (no campaignId), so campaignId is accepted for a
   * uniform ranking surface but only the limit is forwarded.
   */
  async getCampaignUsersMostSubscribers(
    _campaignId: string | undefined,
    rankingFetchLimit: number,
  ): Promise<JsonObject[]> {
    return this.campaignsDataService.getCampaignUsersMostSubscribers(
      rankingFetchLimit,
    );
  }

  /**
   * Ended-campaign user leaderboard from the frozen ranking subcollection.
   */
  async getEndedCampaignUsersMostSubscribers(
    campaignId: string,
    rankingFetchLimit: number,
  ): Promise<JsonObject[]> {
    return this.campaignsDataService.getEndedCampaignUsersMostSubscribers(
      campaignId,
      rankingFetchLimit,
    );
  }

  /**
   * Live content leaderboard ordered by likes.
   */
  async getCampaignContentMostLikes(
    campaignId: string,
    rankingFetchLimit: number,
  ): Promise<JsonObject[]> {
    return this.campaignsDataService.getCampaignContentMostLikes(
      campaignId,
      rankingFetchLimit,
    );
  }

  /**
   * Ended-campaign content leaderboard (likes) from the frozen ranking subcollection.
   */
  async getEndedCampaignContentMostLikes(
    campaignId: string,
    rankingFetchLimit: number,
  ): Promise<JsonObject[]> {
    return this.campaignsDataService.getEndedCampaignContentMostLikes(
      campaignId,
      rankingFetchLimit,
    );
  }

  /**
   * Live content leaderboard ordered by coins.
   */
  async getCampaignContentMostCoins(
    campaignId: string,
    rankingFetchLimit: number,
  ): Promise<JsonObject[]> {
    return this.campaignsDataService.getCampaignContentMostCoins(
      campaignId,
      rankingFetchLimit,
    );
  }

  /**
   * Ended-campaign content leaderboard (coins) from the frozen ranking subcollection.
   */
  async getEndedCampaignContentMostCoins(
    campaignId: string,
    rankingFetchLimit: number,
  ): Promise<JsonObject[]> {
    return this.campaignsDataService.getEndedCampaignContentMostCoins(
      campaignId,
      rankingFetchLimit,
    );
  }
}

// Export singleton instance for convenience
export const campaignsService = CampaignsService.getInstance();
